import {
  Button,
  Popconfirm,
  Space,
  type TableColumnsType,
  Tag,
  Typography,
} from 'antd'

import { formatUsdHourly, formatUsdPerHour, hardwareLabel } from '../format'
import type { GpuOffer } from '../hooks/useGpuAdmin'

const baseColumns: TableColumnsType<GpuOffer> = [
  {
    title: 'Offer',
    dataIndex: 'native_offer_id',
    render: (id: string | null, offer) => (
      <Space direction="vertical" size={0}>
        <Typography.Text copyable={{ text: offer.id }}>
          {id ?? offer.id}
        </Typography.Text>
        <Typography.Text type="secondary">{`#${offer.provider_offer_id}`}</Typography.Text>
      </Space>
    ),
  },
  {
    title: 'Hardware',
    dataIndex: 'hardware',
    width: 280,
    render: (hw: GpuOffer['hardware']) => hardwareLabel(hw),
  },
  {
    title: 'Region',
    key: 'region',
    render: (_, o) =>
      [o.region, o.country_code, o.offer_kind].filter(Boolean).join(' · '),
  },
  {
    title: 'Provider price',
    key: 'native_price',
    align: 'right',
    render: (_, o) =>
      formatUsdPerHour(o.native_price, o.native_price_tick_length_seconds),
  },
  {
    title: 'Sale price',
    key: 'sale_price',
    align: 'right',
    render: (_, o) =>
      formatUsdPerHour(o.sale_price, o.sale_price_tick_length_seconds),
  },
  { title: 'Available', dataIndex: 'available', align: 'right' },
  {
    title: 'On sale',
    key: 'on_sale',
    render: (_, o) => (
      <Space size={4} wrap>
        {o.on_sale ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>}
        {o.offer_excluded && <Tag color="red">Taken off sale</Tag>}
        {o.country_excluded && <Tag color="red">Country excluded</Tag>}
        {o.stale && <Tag color="orange">Stale</Tag>}
      </Space>
    ),
  },
  {
    title: 'Akash market',
    dataIndex: 'market_evidence',
    render: (evidence: GpuOffer['market_evidence']) =>
      evidence ? (
        <Space direction="vertical" size={0}>
          <Space size={4}>
            <Tag color={evidence.state === 'confirmed' ? 'blue' : 'default'}>
              {evidence.state}
            </Tag>
            {formatUsdHourly(evidence.price)}
          </Space>
          <Typography.Text type="secondary">
            {`${evidence.leases} leases · ${evidence.owners} owners`}
          </Typography.Text>
        </Space>
      ) : (
        '—'
      ),
  },
]

interface Actions {
  onTakeOff: (offer: GpuOffer) => void
  onReturn: (offer: GpuOffer) => Promise<unknown>
}

// Exclusions key on provider_offer_id, which survives catalog re-issue.
export function offerColumns(
  actions: Actions | undefined,
  withMarket: boolean,
): TableColumnsType<GpuOffer> {
  const base = withMarket
    ? baseColumns
    : baseColumns.filter((c) => c.title !== 'Akash market')
  if (!actions) return base
  return [
    ...base,
    {
      key: 'action',
      width: 140,
      fixed: 'right',
      render: (_, o) =>
        o.offer_excluded ? (
          <Popconfirm
            title={`Return offer ${o.provider_offer_id} to sale?`}
            okText="Return to sale"
            onConfirm={() => actions.onReturn(o).catch(() => undefined)}
          >
            <Button size="small">Return to sale</Button>
          </Popconfirm>
        ) : (
          <Button size="small" danger onClick={() => actions.onTakeOff(o)}>
            Take off sale
          </Button>
        ),
    },
  ]
}
